import { useEffect, useRef, useState } from "react";
import Header from "./ui/Header";
import { useLocation, useNavigate } from "react-router-dom";

import SecondaryCTAButtons from "./ui/SecondaryCTAButtons";
import FilterMailCircleIcon from "../assets/icons/filter-mail-circle-solid-standard";
import HorseAdCard from "./ui/HorseAdCard";
import { extractAndSetCountriesFromAds, HorseAd } from "../classes/HorseAd";
import HorseDetails from "./HorseDetails";
import {
  getAdById,
  getDocsFromFirestoreWithSortWithoutLimit,
} from "../services/firebase";
import FilterNew from "./ui/FilterNew";
import RelatedAds from "./ui/RelatedAds";
import { Stack, Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";

const Home = () => {
  const [selectedAd, setSelectedAd] = useState<HorseAd | null>(null);
  const [ads, setAds] = useState<HorseAd[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [filteredAds, setFilteredAds] = useState<HorseAd[]>(ads);
  const navigate = useNavigate();
  const location = useLocation();

  // Filter states
  const [age, setAge] = useState<number | undefined>(undefined);
  const [category, setCategory] = useState<string[]>([]);
  const [studbook, setStudbook] = useState<string[]>([]);
  const [examination, setExamination] = useState<string[]>([]);
  const [level, setLevel] = useState<string[]>([]);
  const [gender, setGender] = useState<string[]>([]);
  const [color, setColor] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);

  const [minAge, setMinAge] = useState<number | undefined>(undefined);
  const [maxAge, setMaxAge] = useState<number | undefined>(undefined);

  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const [minPrice, setMinPrice] = useState<number | undefined>(undefined);
  const [maxPrice, setMaxPrice] = useState<number | undefined>(undefined);

  const [includePricePerRequest, setIncludePricePerRequest] =
    useState<boolean>(true);

  // const [skipInitialFetch, setSkipInitialFetch] = useState(false);
  const skipFetchRef = useRef(false);

  const fetchAndProcessAds = async () => {
    try {
      // Step 1: Fetch all documents, initially sorted by createdtime in descending order
      const res = await getDocsFromFirestoreWithSortWithoutLimit(
        "createdtime",
        "desc"
      );
      const docs = res.docs;

      console.log("Fetched docs:", docs.length);

      // Step 2: Separate documents into those with and without lastPaymentTime
      const docsWithLastPaymentTime = docs.filter(
        (doc) => doc.data().lastPaymentTime
      );
      const docsWithoutLastPaymentTime = docs.filter(
        (doc) => !doc.data().lastPaymentTime
      );

      // Step 3: Sort docs with lastPaymentTime by lastPaymentTime in descending order
      docsWithLastPaymentTime.sort(
        (a, b) => b.data().lastPaymentTime - a.data().lastPaymentTime
      );

      // Step 4: Combine sorted arrays: docs with lastPaymentTime first, then docs without it
      const combinedDocs = [
        ...docsWithLastPaymentTime,
        ...docsWithoutLastPaymentTime,
      ];

      // Step 5: Remove duplicates by creating a unique set of document IDs
      const uniqueDocs = Array.from(
        new Set(combinedDocs.map((doc) => doc.id))
      ).map((id) => combinedDocs.find((doc) => doc.id === id)!); // The '!' ensures that doc is not undefined

      // Step 6: Map documents to HorseAd model and filter out ads that don't meet criteria
      const fetchedAds = uniqueDocs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<HorseAd, "id">),
      })) as HorseAd[];

      // Step 7: Apply additional filters (similar to Swift's `isActiveAd` check)
      const activeAds = fetchedAds.filter((ad: HorseAd) => isActiveAd(ad));

      // Step 8: Update state with filtered ads
      setAds(activeAds);
      setFilteredAds(activeAds);

      // Setting countries for filter
      setAvailableCountries(extractAndSetCountriesFromAds(activeAds));
      console.log("AVAILABLE_COUNTRIES: ", availableCountries);
    } catch (error) {
      console.error("Error fetching ads:", error);
    }
  };
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get("id");
    console.log("ID from URL:", idFromUrl);

    if (idFromUrl) {
      skipFetchRef.current = true; // Immediately blocks initial full-fetch
      getAdById(idFromUrl).then((ad) => {
        if (ad && isActiveAd(ad)) {
          setSelectedAd(ad);
          setTimeout(() => {
            topElementRef.current?.scrollIntoView({ behavior: "smooth" });
          }, 200);
        } else {
          alert("Advertentie niet gevonden of niet actief.");
          navigate("/home"); // fallback to all ads
          skipFetchRef.current = false; // allow full fetch after alert
          fetchAndProcessAds(); // manually trigger fetch
        }
      });
    } else {
      fetchAndProcessAds(); // no id in URL, fetch everything
    }
  }, [location.search]);

  // useEffect(() => {
  //   fetchAndProcessAds();
  // }, []);

  function isPaid(ad: HorseAd): boolean {
    const expiryTime = ad?.expiryTime;
    const currentTime = Date.now(); // Gets the current time in milliseconds

    const expTime = expiryTime ?? 0;

    if (expTime == 0) {
      return true;
    }

    if (currentTime < expTime) {
      return true;
    }

    return false;
  }

  const isActiveAd = (ad: HorseAd): boolean => {
    return ad.status < 2 && isPaid(ad);
  };

  const adOnClick = (ad: HorseAd) => {
    setSelectedAd(ad);
    navigate(`/home?id=${ad.id}`); // this updates the URL with the ad ID
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idFromUrl = params.get("id");

    if (idFromUrl && ads.length > 0) {
      const foundAd = ads.find((ad) => ad.id === idFromUrl);
      if (foundAd) {
        setSelectedAd(foundAd);
        setTimeout(() => {
          topElementRef?.current?.scrollIntoView({ behavior: "smooth" });
        }, 200);
      }
    }
  }, [location.search, ads]);

  const closeDetailsView = () => {
    console.log("close button called");
    setSelectedAd(null);
    navigate("/home"); // Clears the ID from URL
  };

  const openSellHorsePage = () => {
    navigate("/sell");
    // window.open("https://sell.allhorses.nl", "_blank");
  };

  const adRelatedAdOnClick = (ad: HorseAd) => {
    setSelectedAd(ad);
    navigate(`/home?id=${ad.id}`);
  };

  const topElementRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (topElementRef.current) {
      topElementRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 52;
  const pageCount = Math.ceil(filteredAds.length / itemsPerPage);
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAds = filteredAds.slice(startIndex, startIndex + itemsPerPage);

  const { t } = useTranslation();

  return (
    <div className="mainFrame">
      <Header />
      <div className="flex justify-center items-center p-1 bg-[#ffffff00] relative">
        {/* For transparent edges */}

        <div className="flex flex-col py-2 md:px-7  rounded-3xl relative ">
          {/* The Main Container */}
          {selectedAd == null && (
            <div className="flex flex-col justify-between items-center pb-8 sm:flex-row sm:space-y-0 sm:space-x-4">
              {/* Main CTA Container */}
              {/* <h1 className="font-semibold text-center sm:text-left sm:mt-4 sm:text-sm text-base md:text-xl lg:text-2xl w-full sm:w-[49vw] mb-4 sm:mb-0 text-white">
                Bereik duizenden potentiële kopers voor jouw paard of pony. Bij
                ons download je eenvoudig handige aan- en verkoopcontracten,
                profiteer je van korting bij SMDC via Allhorses, en ontdek je
                nog veel meer voordelen. Plaats vandaag nog je advertentie!
              </h1> */}
              <h1 className="font-semibold text-center sm:text-left sm:mt-4 sm:text-sm text-base md:text-xl lg:text-2xl w-full sm:w-[49vw] mb-4 sm:mb-0 text-white">
                Bereik duizenden potentiële kopers voor jouw paard of pony.
                {/* {t("headline")} */}
                <br />
                Bij ons download je eenvoudig handige aan- en verkoopcontracten,
                profiteer je van korting bij SMDC via Allhorses, en ontdek je
                nog veel meer voordelen.
                {/* {t("benefits")} */}
                <br />
                Plaats vandaag nog je advertentie!
                {/* {t("call_to_action")} */}
              </h1>

              <SecondaryCTAButtons
                label={showFilter ? "Verberg Filter" : "Toon Filter"}
                onClick={() => setShowFilter(!showFilter)}
                ButtonIcon={FilterMailCircleIcon}
                className="h-14 w-auto"
                displayIcon={true}
              ></SecondaryCTAButtons>
            </div>
          )}

          {showFilter && (
            <div className={`filter-container ${showFilter ? "show" : ""}`}>
              {/* Pass them to the Filter component */}
              <FilterNew
                ads={ads}
                setFilteredAds={setFilteredAds}
                hideFilter={() => setShowFilter(false)}
                age={age}
                setAge={setAge}
                category={category}
                setCategory={setCategory}
                studbook={studbook}
                setStudbook={setStudbook}
                examination={examination}
                setExamination={setExamination}
                level={level}
                setLevel={setLevel}
                gender={gender}
                setGender={setGender}
                color={color}
                setColor={setColor}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                minHeight={minHeight}
                setMinHeight={setMinHeight}
                maxHeight={maxHeight}
                setMaxHeight={setMaxHeight}
                minAge={minAge}
                setMinAge={setMinAge}
                maxAge={maxAge}
                setMaxAge={setMaxAge}
                countries={countries}
                setCountries={setCountries}
                includePricePerRequest={includePricePerRequest}
                setIncludePricePerRequest={setIncludePricePerRequest}
                AVAILABLE_COUNTRIES={availableCountries}
              />
            </div>
            // </div>
          )}

          <div className="flex flex-col items-center justify-center pt-0  rounded-3xl min-h-[38em] overflow-y-auto md:w-full sm:w-full lg:w-[90vw]">
            {/* Horse Card Container */}
            {ads.length === 0 && selectedAd == null ? (
              <p className="text-center text-white opacity-70">
                Even geduld, advertenties worden geladen ...
                {/* {t("loadingAds")} */}
              </p>
            ) : filteredAds.length === 0 && selectedAd == null ? (
              <p className="text-center text-white opacity-70">
                Geen advertenties gevonden voor het geselecteerde filter!
                {/* {t("filterError")} */}
              </p>
            ) : selectedAd == null ? (
              <>
                <div className="h-full flex flex-wrap justify-evenly  w-[inherit]">
                  {paginatedAds.map((ad: HorseAd) => (
                    <HorseAdCard
                      key={ad.name + ad.id}
                      ad={ad}
                      onClick={() => adOnClick(ad)}
                      isMyHorsesPage={false}
                    />
                  ))}
                </div>

                {/* Material-UI Pagination */}
                <Stack spacing={2} className="mt-4 flex-row justify-center">
                  <Pagination
                    count={pageCount}
                    color="primary"
                    onChange={handlePageChange}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#fff", // Default text color (white) for non-selected items
                        "&:hover": {
                          backgroundColor: "#ee5529", // Hover background color
                          color: "#fff", // Text color on hover (white)
                        },
                      },
                      "& .Mui-selected, & .MuiPaginationItem-root.Mui-selected":
                        {
                          backgroundColor: "#ee5529", // Active button background color
                          color: "#fff", // Active text color (white)
                        },
                    }}
                  />
                </Stack>
              </>
            ) : (
              <></>
            )}

            {selectedAd !== null && (
              <div className="rounded-3xl " ref={topElementRef}>
                <HorseDetails
                  key={selectedAd.id}
                  ad={selectedAd}
                  onClickClose={closeDetailsView}
                  onClickScrollTop={scrollToTop}
                />
                <RelatedAds
                  thisHorse={selectedAd}
                  ads={ads}
                  adRelatedAdOnClick={adRelatedAdOnClick}
                />
              </div>
            )}
          </div>

          {/* <div
            className="w-[32vw] flex justify-around items-center px-6 py-4 bg-[#F0663E] rounded-full cursor-pointer transition-all duration-300 ease-in-out hover:bg-[#cf6044] hover:shadow-lg absolute bottom-8 left-[34vw] z-10"
            onClick={openSellHorsePage}
          >
            <Add01Icon
              className="text-white mr-2 transition-transform duration-300 ease-in-out hover:rotate-12"
              size={26}
              color={"#FFFFFF"}
            />
            <p className="font-semibold sm:text-lg md:text-lg lg:text-xl text-white transition-colors duration-300 ease-in-out">
              Showcase Your Horse to Thousands!
            </p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Home;
